import React from 'react'
import { NPaymentProviderMethodDefault } from '@/api/NPayments/Types/NPaymentProviderDto'
import { TransactionDirectionType } from '@/api/Transaction/Types/TransactionDirectionType'
import { WalletCurrencyDto } from '@/api/Wallet/Types/WalletCurrencyDto'
import { WalletTypeDto } from '@/api/Wallet/Types/WalletTypeDto'
import {
  FormBaseAntSelectWrapper,
  FormBaseButtonActionWrapper,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
  FormBaseSectionBlock,
} from '@/components/Form/Form.styles'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'

interface NPaymentProviderMethodDefaultFormValues {
  id?: string
  walletTypeId?: number
  currencyId?: string
  typeId?: number
  minimumAmount: number
  maximumAmount: number
  enabled: boolean
  maintenance: boolean
}
interface MethodDefaultFormProps {
  v: NPaymentProviderMethodDefaultFormValues
  onSubmit: (
    values: NPaymentProviderMethodDefault,
    actions: FormikHelpers<NPaymentProviderMethodDefaultFormValues>
  ) => void
  onClose: () => void
  walletType: WalletTypeDto[]
  directionTypes: TransactionDirectionType[]
  walletCurrencies: WalletCurrencyDto[]
}

const validationSchema = Yup.object().shape({
  walletTypeId: Yup.string().required('Wallet Type is required'),
  currencyId: Yup.string().required('Currency is required'),
  typeId: Yup.string().required('Type is required'),
  minimumAmount: Yup.number()
    .min(0, 'Minimum must be 0 or greater')
    .required('Minimum is required'),
  maximumAmount: Yup.number()
    .moreThan(Yup.ref('minimum'), 'Maximum must be greater than Minimum')
    .positive('Maximum must be greater than 0')
    .required('Maximum is required'),
  enabled: Yup.boolean(),
  maintenance: Yup.boolean(),
})

const PaymentProviderMethodDefaultForm: React.FC<MethodDefaultFormProps> = ({
  v,
  onSubmit,
  onClose,
  walletType,
  walletCurrencies,
  directionTypes,
}) => {
  const initialValues: NPaymentProviderMethodDefaultFormValues = {
    ...v,
  }

  const dataToSubmit = (
    v: NPaymentProviderMethodDefaultFormValues
  ): NPaymentProviderMethodDefault => {
    let walletTypeDto = walletType.find((w) => w.id === Number(v.walletTypeId))
    let paymentCurrency = walletCurrencies.find((c) => c.id === v.currencyId)
    let configurationType = directionTypes.find((d) => d.id.toString() === v.typeId?.toString())

    if (!walletTypeDto) {
      walletTypeDto = walletType[0]
    }
    if (!paymentCurrency) {
      paymentCurrency = walletCurrencies[0]
    }
    if (!configurationType) {
      configurationType = directionTypes[0]
    }
    return {
      id: v.id,
      walletType: walletTypeDto,
      configurationType,
      enabled: v.enabled,
      maintenance: v.maintenance,
      maximumAmount: v.maximumAmount,
      minimumAmount: v.minimumAmount,
      paymentCurrency: paymentCurrency,
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, formikHelpers) => onSubmit(dataToSubmit(values), formikHelpers)}
    >
      {({ errors, touched, isSubmitting, resetForm, values }) => (
        <Form>
          <FormBaseSectionBlock>
            <FormBaseFieldLabel>{initialValues.id ? `Edit` : 'Add New Option'}</FormBaseFieldLabel>

            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Wallet Type</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <FormBaseAntSelectWrapper>
                    <Field component='select' name='walletTypeId' value={values.walletTypeId}>
                      <option value=''>Select Wallet Type</option>
                      {walletType.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </Field>
                  </FormBaseAntSelectWrapper>
                </FormBaseFieldGroup>
              </FormBaseRow>
              {errors.walletTypeId && touched.walletTypeId && (
                <FormBaseErrorMessage>{errors.walletTypeId}</FormBaseErrorMessage>
              )}
            </FormBaseFieldBlock>

            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Currency</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <FormBaseAntSelectWrapper>
                    <Field component='select' name='currencyId'>
                      <option value=''>Select Currency</option>
                      {walletCurrencies.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.id}
                        </option>
                      ))}
                    </Field>
                  </FormBaseAntSelectWrapper>
                </FormBaseFieldGroup>
              </FormBaseRow>
              {errors.currencyId && touched.currencyId && (
                <FormBaseErrorMessage>{errors.currencyId}</FormBaseErrorMessage>
              )}
            </FormBaseFieldBlock>

            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Type</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <FormBaseAntSelectWrapper>
                    <Field component='select' name='typeId'>
                      <option value=''>Select Type</option>
                      {directionTypes.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </Field>
                  </FormBaseAntSelectWrapper>
                </FormBaseFieldGroup>
              </FormBaseRow>
              {errors.typeId && touched.typeId && (
                <FormBaseErrorMessage>{errors.typeId}</FormBaseErrorMessage>
              )}
            </FormBaseFieldBlock>

            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Minimum</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='minimumAmount' type='number' step='0.01' />
                </FormBaseFieldGroup>
              </FormBaseRow>
              {errors.minimumAmount && touched.minimumAmount && (
                <FormBaseErrorMessage>{errors.minimumAmount}</FormBaseErrorMessage>
              )}
            </FormBaseFieldBlock>

            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Maximum</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='maximumAmount' type='number' step='0.01' />
                </FormBaseFieldGroup>
              </FormBaseRow>
              {errors.minimumAmount && touched.minimumAmount && (
                <FormBaseErrorMessage>{errors.minimumAmount}</FormBaseErrorMessage>
              )}
            </FormBaseFieldBlock>

            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Enabled</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='enabled' type='checkbox' />
                </FormBaseFieldGroup>
              </FormBaseRow>
              {errors.enabled && touched.enabled && (
                <FormBaseErrorMessage>{errors.enabled}</FormBaseErrorMessage>
              )}
            </FormBaseFieldBlock>

            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Maintenance</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field name='maintenance' type='checkbox' />
                </FormBaseFieldGroup>
              </FormBaseRow>
              {errors.maintenance && touched.maintenance && (
                <FormBaseErrorMessage>{errors.maintenance}</FormBaseErrorMessage>
              )}
            </FormBaseFieldBlock>

            <FormBaseButtonActionWrapper>
              <ButtonGroup>
                <Button
                  variant='outline'
                  type='button'
                  onClick={() => {
                    onClose()
                    resetForm()
                  }}
                >
                  Cancel
                </Button>
                <Button disabled={isSubmitting} key='button' type='submit'>
                  Save
                </Button>
              </ButtonGroup>
            </FormBaseButtonActionWrapper>
          </FormBaseSectionBlock>
        </Form>
      )}
    </Formik>
  )
}

export default PaymentProviderMethodDefaultForm
